import React from 'react';
import FormFieldTypes from './../constants/FormFieldTypes';
import SectionInfoTypeToFormFieldType from './../constants/SectionInfoTypeToFormFieldType';
import Utils from './../../../utils';

/**
 * ReactFormGeneratorHelper
 */
const ReactFormGeneratorHelper = {

    /**
     * Converts form fields from sectionInfo config to the new form fields config as introduced by the new Form component
     * @param {Object} response - the response object
     * @param {Object} [translations] translations - an optional translation object (defaults to field.name if translation fails)
     * @param {Object} [translations.i18n] translations - an optional translation object (defaults to field.name if translation fails)
     * @param {string} [translations.prefix] translations - an optional translation object (defaults to field.name if translation fails)
     * @param {Object} [fieldMapping] fieldMapping - map custom field attributes
     * @param {Object} [fieldMapping.field] fieldMapping - map custom field attributes, key value pair. e.g. ({ oldKey: 'newKey' })
     * @param {Object} [fieldMapping.valueOptions] fieldMapping - map custom field attributes, key value pair. e.g. ({ oldKey: 'newKey' })
     * @param {Object} [classes] classes
     * @returns {Object} schema - the converted schema
     */
    sectionInfoResponseToFormSchema(
        response,
        translations = {
            i18n: {},
            prefix: ''
        },
        fieldMapping = {
            field: {},
            valueOptions: {}
        },
        classes
    ) {
        const schema = { fields: {} };

        response.fields.forEach(field => {
            const fieldKey = Object.keys(field)[0];
            const fieldObj = field[Object.keys(field)];
            const fieldType = SectionInfoTypeToFormFieldType[fieldObj.type];

            schema.fields[fieldKey] = {

                // id: fieldObj.handle, /* we deliberately stopped doing this in the old Form component since the handle wasnt always the same as the key, which was needed to post a valid form */
                id: fieldKey,
                title: translations.i18n[`${translations.prefix}${Utils.camelCaseToSnakeCase(fieldObj.handle)}`] || fieldObj.name,
                type: FormFieldTypes[fieldType],
                required: fieldObj.form.all.required || false,
                multiple: fieldObj.form.all.multiple || false,
                validator: fieldObj.validator || {},
                defaultValue: fieldObj.value || null,
                sectionGroup: fieldObj.sectionGroup || null,
                disabled: !!field[fieldKey].form.all.disabled,
                ...this.mapObjectFields(field, fieldMapping.field || {})
            };

            // add choices for select field type
            if (FormFieldTypes[fieldType] === FormFieldTypes.SELECT) {
                const selectChoices = [];

                Object.keys(fieldObj.form.all.choices).forEach(choice => {

                    selectChoices.push({
                        key: fieldObj.form.all.choices[choice],
                        label: translations.i18n[`${translations.prefix}${Utils.camelCaseToSnakeCase(fieldObj.form.all.choices[choice])}`] || fieldObj.form.all.choices[choice],
                        disabled: false
                    });
                });

                schema.fields[fieldKey].valueOptions = selectChoices;
            }

            if (FormFieldTypes[fieldType] === FormFieldTypes.RELATIONSHIP) {
                let defaultMultiple = false;
                let selectedValue = null;
                const choices = [];

                if (field[fieldKey].kind === 'one-to-many' || field[fieldKey].kind === 'many-to-many') {
                    defaultMultiple = true;
                    selectedValue = [];
                    schema.fields[fieldKey].multiple = true;
                }

                let useKey = fieldKey;

                // override fieldKey with the 'to' key in case querying the object didnt yield results
                if (!fieldObj[fieldKey]) {
                    useKey = field[fieldKey].to;
                }

                fieldObj[useKey].forEach(choice => {
                    // NOTE: the hidden prop is string boolean. "true"
                    if (!!choice.hidden) {
                        return
                    }

                    let label = choice.label || choice.name; // prefer label over name keu

                    // if there is a translation key, always use that over any other value
                    if (choice.translationKey) {
                        label = translations.i18n[`${translations.prefix}${choice.translationKey}`] || choice.translationKey;
                    }

                    // if there is an alias for a product, use that value, with the original name in small letters
                    if (choice.productAlias) {
                        const labelWithAlias = <>{choice.productAlias} <span className={ classes && classes.productAlias ? classes.productAlias : '' } dangerouslySetInnerHTML={{ __html: ` (${label})` }} /></>;

                        label = labelWithAlias;
                    }

                    choices.push({
                        key: choice.slug,
                        label,
                        disabled: false,
                        selected: choice.selected,
                        ...this.mapObjectFields(choice, fieldMapping.valueOptions || {})
                    });

                    if (choice.selected) {
                        if (defaultMultiple) {
                            selectedValue.push(choice.slug);
                        } else {
                            selectedValue = choice.slug;
                        }
                    }
                });

                schema.fields[fieldKey].valueOptions = choices;
                schema.fields[fieldKey].defaultValue = selectedValue;
            }
        });

        return schema;
    },

    /**
     * @param {Object} obj - object to map fields on
     * @param {Object} [fieldMapping] fieldMapping - map custom field attributes
     * @param {Object} [fieldMapping.field] fieldMapping - map custom field attributes, key value pair. e.g. ({ oldKey: 'newKey' })
     * @param {Object} [fieldMapping.valueOptions] fieldMapping - map custom field attributes, key value pair. e.g. ({ oldKey: 'newKey' })
     * @returns {Object} mapped object - will only return mapped fields
     */
    mapObjectFields(obj, fieldMapping) {
        let mappedObject = {};

        Object.keys(fieldMapping).forEach(key => {
            if (obj[key]) {
                mappedObject[fieldMapping[key]] = obj[key];
            }
        });

        return mappedObject;
    },

    getSectionInfoEndpoint(prefix, options = {}) {
        let endpoint = prefix;

        if (options.section) {
            endpoint = `${endpoint}/${options.section}`;
        }

        if (options.id) {
            endpoint = `${endpoint}/${options.id}`;
        }

        return endpoint;
    },

    getFormErrors(schema, errors, i18n) {
        const formErrors = {};

        // extract all fields from the received errors object that are matched with fields in the schema
        Object.keys(errors).forEach(error => {
            if (Object.keys(schema.fields).includes(error)) {
                const errorTranslationKey = errors[error][0];

                formErrors[error] = { errorText: i18n[errorTranslationKey] || errorTranslationKey };
            }
        });

        return formErrors;
    }
};

export default ReactFormGeneratorHelper;
